<template>
  <div
    class="manager-detail-modern"
    role="main"
    aria-label="Détail du responsable logistique"
  >
    <!-- Modern Skeleton Loading State -->
    <div
      v-if="isLoading"
      class="skeleton-loading"
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <!-- Skeleton Page Header -->
      <div class="skeleton-header mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="skeleton-title mb-2" />
            <div class="skeleton-subtitle" />
          </div>
          <div class="skeleton-actions">
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn" />
          </div>
        </div>
      </div>

      <!-- Skeleton Profile Layout -->
      <b-row class="mb-4">
        <b-col lg="4">
          <!-- Skeleton Profile Card -->
          <div class="skeleton-profile-card">
            <div class="skeleton-profile-header">
              <div class="skeleton-avatar" />
              <div class="skeleton-name" />
              <div class="skeleton-role" />
              <div class="skeleton-status" />
            </div>
            <div class="skeleton-profile-stats">
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Skeleton Information Cards -->
          <div class="skeleton-info-grid">
            <!-- Contact Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Info Card -->
            <div class="skeleton-info-card skeleton-info-card-full">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value skeleton-info-value-long" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="error-container"
      aria-live="assertive"
      role="alert"
    >
      <div class="error-content">
        <div class="error-icon">
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
          />
        </div>
        <h3 class="error-title">
          Erreur
        </h3>
        <p class="error-message">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Content loaded successfully -->
    <div v-else>
      <!-- Modern Page Header -->
      <div class="page-header mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h2
              id="manager-profile-title"
              class="page-title mb-1"
            >
              Profil Responsable Logistique
            </h2>
            <p
              id="manager-profile-desc"
              class="page-subtitle text-muted mb-0"
            >
              Informations détaillées et gestion du responsable logistique.
            </p>
          </div>
          <div class="header-actions">
            <b-button
              variant="warning"
              size="lg"
              class="btn-modern mr-2"
              :to="{ name: 'manager-edit', params: { id: manager.id }}"
              :aria-label="`Modifier le responsable logistique ${manager.username}`"
            >
              <feather-icon
                icon="EditIcon"
                size="16"
                class="mr-2"
                aria-hidden="true"
              />
              Modifier
            </b-button>
            <b-button
              variant="info"
              size="lg"
              class="btn-modern mr-2"
              :aria-label="`Changer le mot de passe de ${manager.username}`"
              @click="openPasswordModal"
            >
              <feather-icon
                icon="KeyIcon"
                size="16"
                class="mr-2"
                aria-hidden="true"
              />
              Mot de passe
            </b-button>
            <b-button
              :variant="manager.is_active ? 'secondary' : 'success'"
              size="lg"
              class="btn-modern"
              :aria-label="manager.is_active ? 'Désactiver le responsable logistique ' + manager.username : 'Activer le responsable logistique ' + manager.username"
              @click="toggleManagerStatus"
            >
              <feather-icon
                :icon="manager.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
                size="16"
                class="mr-2"
                aria-hidden="true"
              />
              {{ manager.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Modern Profile Card -->
      <b-row class="mb-4">
        <b-col lg="4">
          <div class="profile-card-modern">
            <div class="profile-header">
              <div class="profile-avatar">
                <div
                  class="avatar-circle-large"
                  :aria-label="'Avatar de ' + (manager.username || 'Sans nom')"
                  role="img"
                >
                  <span class="avatar-initials-large">{{ getInitials(manager.username) }}</span>
                </div>
                <div
                  class="status-indicator-large"
                  :class="{ active: manager.is_active }"
                  :aria-label="manager.is_active ? 'Responsable actif' : 'Responsable inactif'"
                  role="status"
                />
              </div>
              <h3 class="profile-name">
                {{ manager.username }}
              </h3>
              <p class="profile-role">
                Responsable logistique (Manager de Dépôt)
              </p>
              <div class="profile-status">
                <span
                  class="status-badge-large"
                  :class="manager.is_active ? 'active' : 'inactive'"
                  :aria-label="manager.is_active ? 'Actif' : 'Inactif'"
                >
                  {{ manager.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </div>
            </div>

            <div class="profile-stats">
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="ClockIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Créé le</span>
                  <span class="stat-value">{{ formatDate(manager.created_at) }}</span>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Modifié le</span>
                  <span class="stat-value">{{ formatDate(manager.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Information Cards Grid -->
          <div class="info-cards-grid">
            <!-- Contact Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon contact">
                  <feather-icon
                    icon="MailIcon"
                    size="24"
                    aria-hidden="true"
                  />
                </div>
                <h5
                  id="contact-info-title"
                  class="info-card-title"
                >
                  Informations de Contact
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="MailIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Email</span>
                    <span class="info-value">{{ manager.email || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="PhoneIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">téléphone professionnel</span>
                    <span class="info-value">{{ manager.phone || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="PhoneForwardedIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">téléphone personnel</span>
                    <span class="info-value">{{ manager.phone2 || 'Non défini' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon work">
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="24"
                    aria-hidden="true"
                  />
                </div>
                <h5
                  id="work-info-title"
                  class="info-card-title"
                >
                  Informations Professionnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Entrepôt Assigné</span>
                    <span class="info-value">{{ manager.repository_name || 'Non assigné' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Rôle</span>
                    <span class="info-value">Manager de Dépôt</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon personal">
                  <feather-icon
                    icon="UserIcon"
                    size="24"
                    aria-hidden="true"
                  />
                </div>
                <h5
                  id="personal-info-title"
                  class="info-card-title"
                >
                  Informations Personnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">CIN</span>
                    <span class="info-value">{{ manager.cin || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Adresse</span>
                    <span class="info-value">{{ manager.address || 'Non définie' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Account Security & Audit Card -->
      <b-row>
        <b-col lg="12">
          <b-card
            title="Sécurité du compte & Audit"
            class="mb-4 shadow-sm"
            aria-label="Sécurité du compte et historique des modifications"
          >
            <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 1rem;">
              <div
                v-if="manager.last_password_change"
                class="d-flex align-items-center"
              >
                <div class="avatar bg-light-primary p-2 mr-2">
                  <feather-icon
                    icon="LockIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <div>
                  <strong class="d-block text-dark font-weight-bold">
                    Dernière modification du mot de passe
                  </strong>
                  <span class="small text-muted">
                    Modifié le {{ formatDateTime(manager.last_password_change.created_at) }} par <strong>{{ manager.last_password_change.changed_by_display }}</strong>
                  </span>
                </div>
              </div>
              <div
                v-else
                class="text-muted small d-flex align-items-center"
              >
                <feather-icon
                  icon="ShieldIcon"
                  class="mr-50 text-secondary"
                  size="18"
                />
                <span>Aucune modification de mot de passe enregistrée pour ce compte.</span>
              </div>

              <b-button
                variant="outline-primary"
                size="sm"
                @click="openPasswordModal"
              >
                <feather-icon
                  icon="LockIcon"
                  size="14"
                  class="mr-50"
                />
                <span>Changer le mot de passe</span>
              </b-button>
            </div>

            <!-- Collapsible Audit History List -->
            <div
              v-if="manager.password_change_history && manager.password_change_history.length > 0"
              class="mt-3 pt-2 border-top"
            >
              <b-button
                v-b-toggle.main-password-history-collapse
                variant="flat-primary"
                size="sm"
                class="p-0 font-weight-bold"
              >
                <feather-icon
                  icon="HistoryIcon"
                  size="14"
                  class="mr-25"
                />
                Voir l'historique complet ({{ manager.password_change_history.length }})
              </b-button>

              <b-collapse
                id="main-password-history-collapse"
                class="mt-2"
              >
                <b-list-group
                  flush
                  class="border rounded"
                >
                  <b-list-group-item
                    v-for="log in manager.password_change_history"
                    :key="log.id"
                    class="py-50 px-1 d-flex justify-content-between align-items-center small"
                  >
                    <span>
                      <feather-icon
                        icon="UserCheckIcon"
                        size="12"
                        class="mr-50 text-primary"
                      />
                      <strong>{{ log.changed_by_display }}</strong>
                    </span>
                    <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
                  </b-list-group-item>
                </b-list-group>
              </b-collapse>
            </div>

            <!-- Account audit metadata (create / update timestamps) -->
            <div class="mt-3 pt-2 border-top">
              <b-list-group flush class="mb-0">
                <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                  <span>
                    <feather-icon
                      icon="PlusCircleIcon"
                      size="14"
                      class="mr-50 text-success"
                    />
                    <span class="text-muted">Compte créé le</span>
                  </span>
                  <span class="text-muted">{{ formatDateTime(manager.created_at) }}</span>
                </b-list-group-item>
                <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                  <span>
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="14"
                      class="mr-50 text-primary"
                    />
                    <span class="text-muted">Dernière mise à jour</span>
                  </span>
                  <span class="text-muted">{{ formatDateTime(manager.updated_at) }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Modal for Changing Manager Password -->
    <b-modal
      id="manager-password-modal"
      v-model="showPasswordModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordForm"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div class="avatar bg-light-primary p-2 mr-2">
              <feather-icon
                icon="LockIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0">
                Changer le mot de passe
              </h5>
              <small
                v-if="manager"
                class="text-muted"
              >
                {{ manager.username }} • {{ manager.email || 'Pas d\'email' }}
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            @click="close"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div v-if="manager">
        <!-- Password Audit Log Banner -->
        <b-alert
          v-if="manager.last_password_change"
          show
          variant="light-primary"
          class="mb-3 border-left-primary shadow-sm"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="mr-2 text-primary"
            />
            <div>
              <strong class="d-block text-dark font-weight-bold">
                Dernière modification du mot de passe
              </strong>
              <span class="small text-muted">
                Modifié le {{ formatDateTime(manager.last_password_change.created_at) }} par <strong>{{ manager.last_password_change.changed_by_display }}</strong>
              </span>
            </div>
          </div>
        </b-alert>

        <!-- Collapsible Audit History -->
        <div
          v-if="manager.password_change_history && manager.password_change_history.length > 0"
          class="mb-3"
        >
          <b-button
            v-b-toggle.password-history-collapse
            variant="flat-primary"
            size="sm"
            class="p-0 font-weight-bold"
          >
            <feather-icon
              icon="HistoryIcon"
              size="14"
              class="mr-25"
            />
            Historique des modifications ({{ manager.password_change_history.length }})
          </b-button>

          <b-collapse
            id="password-history-collapse"
            class="mt-2"
          >
            <b-list-group
              flush
              class="border rounded"
            >
              <b-list-group-item
                v-for="log in manager.password_change_history"
                :key="log.id"
                class="py-50 px-1 d-flex justify-content-between align-items-center small"
              >
                <span>
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                    class="mr-50 text-primary"
                  />
                  <strong>{{ log.changed_by_display }}</strong>
                </span>
                <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-collapse>
        </div>

        <b-form @submit.prevent="savePasswordChange">
          <!-- New Password -->
          <b-form-group label-class="form-label-premium"
            label="Nouveau mot de passe *"
            label-for="modal-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input class="premium-input"
                id="modal-password"
                v-model="passwordForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Entrez le nouveau mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <!-- Password Strength Meter -->
            <div
              v-if="passwordForm.password"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-50">
                <small class="text-muted">Force du mot de passe:</small>
                <small
                  :class="'text-' + passwordStrengthClass"
                  class="font-weight-bold"
                >
                  {{ passwordStrengthText }}
                </small>
              </div>
              <b-progress
                :value="passwordStrength"
                :variant="passwordStrengthClass"
                height="6px"
              />
            </div>
          </b-form-group>

          <!-- Confirm Password -->
          <b-form-group label-class="form-label-premium"
            label="Confirmer le mot de passe *"
            label-for="modal-confirm-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input class="premium-input"
                id="modal-confirm-password"
                v-model="passwordForm.confirm_password"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirmez le mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <small
              v-if="passwordForm.confirm_password && !passwordsMatch"
              class="text-danger mt-50 d-block"
            >
              Les mots de passe ne correspondent pas.
            </small>
          </b-form-group>

          <!-- Generate Password Button -->
          <div class="mb-4">
            <PasswordGenerator
              :user="manager"
              user-type="manager"
              button-text="Générer un mot de passe sécurisé"
              @generated="onPasswordGenerated"
            />
          </div>

          <!-- Action Footer -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="showPasswordModal = false"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              :disabled="isPasswordSubmitting || !isPasswordFormValid"
              type="submit"
            >
              <b-spinner
                v-if="isPasswordSubmitting"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-50"
              />
              Enregistrer le mot de passe
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>

    <!-- Status Confirmation Modal -->
    <b-modal
      ref="statusModal"
      :title="statusModalTitle"
      size="md"
      centered
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
    >
      <p class="text-center mb-3">
        {{ statusModalMessage }}
      </p>
      <div class="text-center">
        <feather-icon
          :icon="manager.is_active ? 'AlertTriangleIcon' : 'CheckCircleIcon'"
          size="48"
          :class="manager.is_active ? 'text-warning' : 'text-success'"
        />
      </div>

      <template #modal-ok>
        <feather-icon
          :icon="manager.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
          size="16"
          class="mr-1"
        />
        {{ manager.is_active ? 'Désactiver' : 'Activer' }}
      </template>

      <template #modal-cancel>
        <feather-icon
          icon="XIcon"
          size="16"
          class="mr-1"
        />
        Annuler
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  BButton,
  BRow,
  BCol,
  BModal,
  BAlert,
  BListGroup,
  BListGroupItem,
  BCollapse,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BProgress,
  BSpinner,
  VBToggle,
} from 'bootstrap-vue'

export default {
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    PasswordGenerator,
    BButton,
    BRow,
    BCol,
    BModal,
    BAlert,
    BListGroup,
    BListGroupItem,
    BCollapse,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BProgress,
    BSpinner,
  },
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      manager: {},
      error: null,
      statusModalTitle: '',
      statusModalMessage: '',
      // Password modal data
      showPasswordModal: false,
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      showPassword: false,
      showConfirmPassword: false,
      isPasswordSubmitting: false,
    }
  },

  computed: {
    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special characters

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
      )
    },

    isPasswordFormValid() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordsMatch
        && this.passwordForm.password.length >= 8
      )
    },

    passwordStrengthState() {
      if (!this.passwordForm.password) return null
      return this.passwordForm.password.length >= 8
    },

    canResetPassword() {
      return this.passwordForm.password
             && this.passwordForm.password.length >= 8
             && this.passwordsMatch
    },
  },

  created() {
    this.fetchManagerDetails()
  },

  methods: {
    async fetchManagerDetails() {
      try {
        const { id } = this.$route.params
        // Use the specialized manager endpoint
        const { data } = await axios.get(`/api/auth/users/managers/${id}/`)
        this.manager = data
        this.isLoading = false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching manager details:', error)
        this.error = 'Erreur lors du chargement des données du manager'
        this.isLoading = false

        if (error.response && error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      }
    },

    getInitials(name) {
      if (!name) return 'U'
      return name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('')
    },

    formatDate(dateString) {
      if (!dateString) return 'Non définie'
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: fr })
    },

    toggleManagerStatus() {
      this.statusModalTitle = this.manager.is_active ? 'Désactiver le manager' : 'Activer le manager'
      this.statusModalMessage = this.manager.is_active
        ? 'Êtes-vous sûr de vouloir désactiver ce manager? Il ne pourra plus accéder à son compte.'
        : 'Êtes-vous sûr de vouloir activer ce manager? Il pourra à nouveau accéder à son compte.'

      this.$refs.statusModal.show()
    },

    async handleStatusConfirmed() {
      try {
        await axios.post(`/api/auth/users/managers/toggle-activation/${this.manager.id}/`)
        this.manager.is_active = !this.manager.is_active
        this.showToast('success', `Le manager a été ${this.manager.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling manager status:', error)
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    // Password modal methods
    openPasswordModal() {
      this.showPasswordModal = true
    },

    resetPasswordForm() {
      this.passwordForm = {
        password: '',
        confirm_password: '',
      }
      this.showPassword = false
      this.showConfirmPassword = false
      this.isPasswordSubmitting = false
    },

    onPasswordGenerated(generatedPassword) {
      this.passwordForm.password = generatedPassword
      this.passwordForm.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return dateStr
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} à ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },

    async savePasswordChange() {
      if (!this.isPasswordFormValid) return

      this.isPasswordSubmitting = true
      try {
        await axios.post(
          `/api/auth/users/managers/change-password/${this.manager.id}/`,
          this.passwordForm,
        )

        const { user } = this.$store.state
        const userDisplayName = user
          ? `${user.username} (${user.role || 'Utilisateur'})`
          : 'Utilisateur connecté'

        const newAuditLog = {
          id: Date.now(),
          changed_by_name: user ? user.username : 'Moi',
          changed_by_display: userDisplayName,
          created_at: new Date().toISOString(),
        }

        this.$set(this.manager, 'last_password_change', newAuditLog)
        if (!this.manager.password_change_history) {
          this.$set(this.manager, 'password_change_history', [])
        }
        this.manager.password_change_history.unshift(newAuditLog)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.showPasswordModal = false
        this.resetPasswordForm()
      } catch (error) {
        const errorMsg = error.response?.data?.password?.[0]
          || error.response?.data?.confirm_password?.[0]
          || error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors du changement de mot de passe'

        this.showToast('danger', errorMsg)
      } finally {
        this.isPasswordSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Modern Manager Detail Styling with Team Parcel Express Branding
.manager-detail-modern {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Skeleton Loading Styles
.skeleton-loading {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Base skeleton animation
@keyframes skeleton-pulse {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-element {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-pulse 1.5s infinite;
  border-radius: 8px;
}

// Skeleton Header
.skeleton-header {
  .skeleton-title {
    @extend .skeleton-element;
    width: 200px;
    height: 40px;
    border-radius: 8px;
  }

  .skeleton-subtitle {
    @extend .skeleton-element;
    width: 300px;
    height: 20px;
    border-radius: 6px;
  }

  .skeleton-actions {
    display: flex;
    gap: 0.5rem;

    .skeleton-btn {
      @extend .skeleton-element;
      width: 120px;
      height: 44px;
      border-radius: 12px;
    }
  }
}

// Skeleton Profile Card
.skeleton-profile-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .skeleton-profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .skeleton-avatar {
      @extend .skeleton-element;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      margin: 0 auto 1.5rem auto;
    }

    .skeleton-name {
      @extend .skeleton-element;
      width: 150px;
      height: 28px;
      margin: 0 auto 0.5rem auto;
      border-radius: 6px;
    }

    .skeleton-role {
      @extend .skeleton-element;
      width: 120px;
      height: 16px;
      margin: 0 auto 1rem auto;
      border-radius: 4px;
    }

    .skeleton-status {
      @extend .skeleton-element;
      width: 80px;
      height: 32px;
      margin: 0 auto;
      border-radius: 16px;
    }
  }

  .skeleton-profile-stats {
    padding: 1.5rem;

    .skeleton-stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .skeleton-stat-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-stat-content {
        flex: 1;

        .skeleton-stat-label {
          @extend .skeleton-element;
          width: 80px;
          height: 12px;
          margin-bottom: 0.5rem;
          border-radius: 4px;
        }

        .skeleton-stat-value {
          @extend .skeleton-element;
          width: 120px;
          height: 16px;
          border-radius: 4px;
        }
      }
    }
  }
}

// Skeleton Information Cards
.skeleton-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .skeleton-info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &.skeleton-info-card-full {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .skeleton-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .skeleton-card-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-card-title {
        @extend .skeleton-element;
        width: 180px;
        height: 20px;
        border-radius: 6px;
      }
    }

    .skeleton-card-body {
      padding: 1.5rem;

      .skeleton-info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .skeleton-info-icon {
          @extend .skeleton-element;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .skeleton-info-content {
          flex: 1;

          .skeleton-info-label {
            @extend .skeleton-element;
            width: 60px;
            height: 12px;
            margin-bottom: 0.5rem;
            border-radius: 4px;
          }

          .skeleton-info-value {
            @extend .skeleton-element;
            width: 150px;
            height: 16px;
            border-radius: 4px;

            &.skeleton-info-value-long {
              width: 250px;
            }
          }
        }
      }
    }
  }
}

// Page Header Styling
.page-header {
  .page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .page-subtitle {
    font-size: 1.1rem;
    color: #6c757d;
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-modern {
    background: linear-gradient(135deg, #0d4399 0%, #0a3370 100%);
    border: none;
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 159, 67, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(255, 159, 67, 0.4);
    }

    &.btn-modern-outline {
      background: white;
      border: 2px solid #e9ecef;
      color: #6c757d;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      &:hover {
        border-color: #0d4399;
        background: #0d4399;
        color: white;
      }
    }
  }
}

// Modern Loading Animation
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;

  .loading-content {
    text-align: center;
  }

  .modern-spinner {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;

    .spinner-ring {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 64px;
      height: 64px;
      margin: 8px;
      border: 8px solid #0d4399;
      border-radius: 50%;
      animation: spinner-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: #0d4399 transparent transparent transparent;

      &:nth-child(1) { animation-delay: -0.45s; }
      &:nth-child(2) { animation-delay: -0.3s; }
      &:nth-child(3) { animation-delay: -0.15s; }
    }
  }

  .loading-text {
    color: #8b92b2;
    font-weight: 500;
    margin-top: 1rem;
  }
}

@keyframes spinner-ring {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Error State
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;

  .error-content {
    text-align: center;
    max-width: 500px;

    .error-icon {
      color: #0d4399;
      margin-bottom: 1rem;
    }

    .error-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 1rem;
    }

    .error-message {
      color: #6c757d;
      font-size: 1.1rem;
      margin-bottom: 2rem;
    }
  }
}

// Modern Profile Card
.profile-card-modern {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .profile-avatar {
      position: relative;
      display: inline-block;
      margin-bottom: 1.5rem;

      .avatar-circle-large {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: linear-gradient(135deg, #0d4399 0%, #0a3370 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 10px 30px rgba(255, 159, 67, 0.3);

        .avatar-initials-large {
          color: white;
          font-weight: 700;
          font-size: 2rem;
        }
      }

      .status-indicator-large {
        position: absolute;
        bottom: 5px;
        right: 5px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 4px solid white;
        background: #0d4399;

        &.active {
          background: #16a34a;
        }
      }
    }

    .profile-name {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }

    .profile-role {
      color: #6c757d;
      font-size: 1rem;
      margin-bottom: 1rem;
    }

    .profile-status {
      .status-badge-large {
        padding: 0.5rem 1.5rem;
        border-radius: 25px;
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.active {
          background: rgba(22, 163, 74, 0.1);
          color: #16a34a;
        }

        &.inactive {
          background: rgba(255, 159, 67, 0.1);
          color: #0d4399;
        }
      }
    }
  }

  .profile-stats {
    padding: 1.5rem;

    .stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: rgba(255, 159, 67, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0d4399;
        margin-right: 1rem;
      }

      .stat-content {
        flex: 1;

        .stat-label {
          display: block;
          font-size: 0.8rem;
          color: #8b92b2;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          display: block;
          font-size: 1rem;
          color: #1f2937;
          font-weight: 600;
        }
      }
    }
  }
}

// Information Cards Grid
.info-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    }

    &:nth-child(3) {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    &.info-card-full {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .info-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .info-card-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;

        &.contact {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        &.work {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        &.personal {
          background: rgba(255, 159, 67, 0.1);
          color: #0d4399;
        }

        &.security {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
        }
      }

      .info-card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
      }
    }

    .info-card-body {
      padding: 1.5rem;

      .info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .info-icon {
          color: #8b92b2;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;

          .info-label {
            display: block;
            font-size: 0.8rem;
            color: #8b92b2;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.25rem;
          }

          .info-value {
            display: block;
            font-size: 1rem;
            color: #1f2937;
            font-weight: 500;
            word-break: break-word;
          }
        }
      }
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .manager-detail-modern {
    padding: 1rem;
  }

  .skeleton-loading {
    padding: 1rem;
  }

  .page-header {
    .page-title {
      font-size: 2rem;
    }

    .header-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .btn-modern {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .skeleton-header {
    .skeleton-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .skeleton-btn {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .info-cards-grid {
    grid-template-columns: 1fr;

    .info-card:nth-child(3) {
      grid-column: 1;
    }
  }

  .skeleton-info-grid {
    grid-template-columns: 1fr;

    .skeleton-info-card.skeleton-info-card-full {
      grid-column: 1;
    }
  }
}

// Security & Audit Section Styles
.security-alert {
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  padding: 1rem;

  &.no-change {
    background: rgba(148, 163, 184, 0.05);
    border-left-color: #94a3b8;
  }

  .security-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(59, 130, 246, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
  }
}
</style>
